import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Column as RdgColumn, SortColumn } from "react-data-grid";

import { EditFileBlueIcon, RoundedPlusSignIcon } from "../../assets";
import {
  InteractionObjectType,
  InteractionType,
  LocalStorageConstants,
  ProjectIssuanceConstants,
  ProjectIssuanceTransactionConstants,
} from "../../constants";
import { CursorChangeProps, DataGridColumnCurrency, SelectData } from "../../models";
import {
  GetProjectIssuancesResponse,
  searchInteractions,
  searchIssuanceTransactions,
  SearchIssuanceTransactionsResponse,
} from "../../service/query";
import { ResultData } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { flattenObject, jsonReviver, recursivelyValidateAndTransformDateToString } from "../../utils";
import {
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
} from "../../widget";
import { EditColumns, IconCellFormatterData } from "../../widget/data/DataGrid/models";
import { UnitTransactionsPanelFilterObjects } from "./UnitTransactionsPanel";

interface UseUnitTransactionsPanelProps {
  issuance?: GetProjectIssuancesResponse["results"][0];
  filter?: UnitTransactionsPanelFilterObjects;
  handleRefresh: () => void;
}

interface UseUnitTransactionsPanelReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;

  showActionsMenu: boolean;
  setShowActionsMenu: Dispatch<SetStateAction<boolean>>;

  showEditIssuanceTransactionPanel: boolean;
  showAddOrLinkIssuanceTransactionPanel: boolean;
  setShowAddOrLinkIssuanceTransactionPanel: Dispatch<SetStateAction<boolean>>;
  handleCloseIssuanceTransactionPanel: () => void;

  issuanceTransaction: SearchIssuanceTransactionsResponse["results"][0] | undefined;
  showEditColumnsPanel: boolean;
  setShowEditColumnsPanel: Dispatch<SetStateAction<boolean>>;
  actionsMenuOptionsList: ProfileMenuItemType[];
  exportFileName: string;
  defaultEditColumns: EditColumns;
  inventoryTransactionsEditColumnsLocalStorageItemName: string;
  exportTable: boolean | undefined;
  filterExportRows: (columns: ResultData[]) => ResultData[];
  filterExportColumns: (columns: RdgColumn<ResultData, unknown>[]) => RdgColumn<ResultData, unknown>[];
  refreshIssuanceTransactions: boolean;
  handleRefreshIssuancesAndIssuanceTransactions: () => void;

  tagsFilterSelectData: SelectData;
  selectedTagKeys: string[];
  onTagsFilter: (keys: string[]) => Promise<void>;
  hasData: boolean;
}

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "transactionDate", direction: "ASC" }];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
  {
    name: "Transaction date",
    key: "transactionDate",
    dataType: "Date",
    formatter: "dateOnly",
    alignment: "left",
    sortable: true,
    filterable: false,
  },
  {
    name: "Counterparty",
    key: "counterParty",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Broker",
    key: "broker",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Type",
    key: "type",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Quantity",
    key: "quantity",
    dataType: "number",
    formatter: "number",
    sortable: true,
    filterable: true,
  },
  {
    name: "Price",
    key: "price",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Fees",
    key: "fees",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Total value",
    key: "totalValue",
    dataType: "number",
    formatter: "currency",
    currency: DataGridColumnCurrency.Gbp,
    sortable: true,
    filterable: true,
  },
  {
    name: "Transaction reference",
    key: "externalReference",
    dataType: "string",
    formatter: "stringWithEllipsis",
    sortable: true,
    filterable: true,
  },
  {
    name: "Settle",
    key: "linkIcon",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    minWidth: 100,
    sortable: false,
  },
  {
    name: "Edit",
    key: "editIcon",
    dataType: "string",
    formatter: "icon",
    alignment: "center",
    filterable: false,
    minWidth: 100,
    sortable: false,
  },
  {
    name: "ID",
    key: "uuid",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: false,
    sortable: false,
  },
  {
    name: "Previous transaction ID",
    key: "parentTransactionUuid",
    dataType: "number",
    filterable: false,
    sortable: false,
  },
  {
    name: "Unit size (Ha or Km)",
    key: "areaHa",
    dataType: "number",
    filterable: true,
    sortable: true,
  },
  {
    name: "Created at",
    key: "createdAt",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Updated at",
    key: "updatedAt",
    dataType: "Date",
    formatter: "dateOnly",
    filterable: false,
    sortable: true,
  },
  {
    name: "Created by",
    key: "createdByUser.fullName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
  {
    name: "Updated by",
    key: "updatedByUser.fullName",
    dataType: "string",
    formatter: "stringWithEllipsis",
    filterable: true,
    sortable: true,
  },
];

const getEditColumns = (): EditColumns => {
  const editColumns = columns.reduce((acc: EditColumns, col, index) => {
    let show = true;
    if (
      col.key === "createdAt" ||
      col.key === "updatedAt" ||
      col.key === "createdByUser.fullName" ||
      col.key === "updatedByUser.fullName" ||
      col.key === "uuid" ||
      col.key === "parentTransactionUuid" ||
      col.key === "areaHa"
    )
      show = false;
    acc[col.key] = { show, index };
    return acc;
  }, {});

  return editColumns;
};

const filterExportRows = (rows: ResultData[]): ResultData[] => {
  // Clone rows and Covert from date to string in dd/mm/yyyy format
  const rowsClone = recursivelyValidateAndTransformDateToString(JSON.parse(JSON.stringify(rows), jsonReviver))
    .data as ResultData[];
  rowsClone.forEach((row) => {
    // eslint-disable-next-line no-param-reassign
    if (row.linkIcon !== undefined) delete row.linkIcon;
    // eslint-disable-next-line no-param-reassign
    if (row.editIcon !== undefined) delete row.editIcon;
  });
  return rowsClone;
};
const filterExportColumns = (rdgColumns: RdgColumn<ResultData, unknown>[]): RdgColumn<ResultData, unknown>[] => {
  return rdgColumns.filter((column) => column.key !== "editIcon" && column.key !== "linkIcon");
};

export const useUnitTransactionsPanel = ({
  issuance,
  filter,
  // eslint-disable-next-line unused-imports/no-unused-vars
  handleRefresh,
}: UseUnitTransactionsPanelProps): UseUnitTransactionsPanelReturnData => {
  const { user, currentOrganisationUuid } = useAuth();
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  const [exportTable, setExportTable] = useState<boolean>();

  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditColumnsPanel, setShowEditColumnsPanel] = useState(false);
  const [showAddOrLinkIssuanceTransactionPanel, setShowAddOrLinkIssuanceTransactionPanel] = useState(false);
  const [showEditIssuanceTransactionPanel, setShowEditIssuanceTransactionPanel] = useState(false);

  const [issuanceTransaction, setIssuanceTransaction] = useState<SearchIssuanceTransactionsResponse["results"][0]>();

  const [refreshIssuanceTransactions, setRefreshIssuanceTransactions] = useState(false);
  const [tagsFilter, setTagsFilter] = useState<string[]>();
  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [selectedTagKeys, setSelectedTagKeys] = useState<string[]>([]);

  const handleRefreshIssuancesAndIssuanceTransactions = (): void => {
    handleRefresh();
    setRefreshIssuanceTransactions(!refreshIssuanceTransactions);
  };

  const handleCloseIssuanceTransactionPanel = (): void => {
    setShowAddOrLinkIssuanceTransactionPanel(false);
    setShowEditIssuanceTransactionPanel(false);
    setIssuanceTransaction(undefined);
    setSelectedTagKeys([]);
    setTagsFilter(undefined);
  };

  const actionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit columns",
      action: () => setShowEditColumnsPanel(true),
    },
    {
      id: 2,
      value: "Export table",
      action: () => setExportTable(!exportTable),
    },
  ];

  const formatData = useCallback((responseData: SearchIssuanceTransactionsResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };

        if (
          d.type !== ProjectIssuanceTransactionConstants.ISSUANCE_TRANSACTION_TYPE_CANCELLED &&
          issuance?.status !== ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE
        ) {
          result.linkIcon = {
            icon: <RoundedPlusSignIcon className="LinkIssuanceIcon" />,
            action: () => {
              setIssuanceTransaction(d);
              setShowAddOrLinkIssuanceTransactionPanel(true);
            },
          } as IconCellFormatterData;
        }

        result.editIcon = {
          icon: <EditFileBlueIcon width={20} height={20} />,
          action: () => {
            setIssuanceTransaction(d);
            setShowEditIssuanceTransactionPanel(true);
          },
        } as IconCellFormatterData;

        return result;
      }) || []
    );
  }, []);

  const onTagsFilter = async (keys: string[]): Promise<void> => {
    setSelectedTagKeys(keys);

    if (keys.length === 0) {
      setTagsFilter(undefined);
      setRefreshIssuanceTransactions((prev) => !prev);
      return;
    }

    const searchInteractionsFilterCriteria = dataGridMapFilterCriteria([]);

    searchInteractionsFilterCriteria.uuid = {
      operator: "in",
      value: keys,
    };

    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: searchInteractionsFilterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilter(
          response.data.results
            .filter((x) => x.objectInteractions.length > 0)
            .map((x) => x.objectInteractions.map((y) => y.objectUuid))
            .flat()
        );
      } else {
        setTagsFilter(undefined);
      }
      setRefreshIssuanceTransactions((prev) => !prev);
    });
  };

  const onChange = async ({
    filtering,
    paging,
    sorting,
  }: CursorChangeProps): Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> => {
    let data = {
      resultData: [] as ResultData[],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (tagsFilter && tagsFilter.length > 0) {
      filterCriteria.uuid = {
        operator: "in",
        value: tagsFilter,
      };
    }

    if (issuance?.uuid) {
      filterCriteria.issuanceUuid = {
        operator: "eq",
        value: issuance.uuid,
      };
    }

    if (filter?.projectUuid) {
      filterCriteria.project = {
        uuid: {
          operator: "eq",
          value: filter.projectUuid,
        },
      };
    }

    if (filter?.vintage) {
      filterCriteria.vintage = {
        operator: "eq",
        value: filter.vintage,
      };
    }

    if (filter?.projectTypeDisplayName) {
      filterCriteria.project = {
        projectType: {
          displayName: {
            operator: "eq",
            value: filter.projectTypeDisplayName,
          },
        },
      };
    }

    if (filter?.quantityType) {
      filterCriteria.quantityType = {
        operator: "eq",
        value: filter.quantityType,
      };
    }

    if (filter?.habitatCondition) {
      filterCriteria.habitatCondition = {
        operator: "eq",
        value: filter.habitatCondition,
      };
    }

    if (filter?.habitatGroup) {
      filterCriteria.habitatGroup = {
        operator: "eq",
        value: filter.habitatGroup,
      };
    }

    if (filter?.distinctiveness) {
      filterCriteria.distinctiveness = {
        operator: "eq",
        value: filter.distinctiveness,
      };
    }

    const sortKeyMap: Record<string, string> = {};

    await searchIssuanceTransactions({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
        setHasData(data.paging.totalCount > 0);
      });

    return data;
  };

  const getOrganisationTags = useCallback(async (organisationUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = {
      operator: "eq",
      value: InteractionType.TAG,
    };
    filterCriteria.objectType = {
      operator: "eq",
      value: InteractionObjectType.ORGANISATION,
    };
    filterCriteria.objectUuid = {
      operator: "eq",
      value: organisationUuid,
    };

    filterCriteria.objectInteractions = {
      some: {
        objectType: {
          operator: "eq",
          value: InteractionObjectType.ISSUANCE_TRANSACTION,
        },
      },
    };
    await searchInteractions({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.data?.results != null && response.data.results.length > 0) {
        setTagsFilterSelectData(
          response.data.results.map((x) => ({
            key: x.uuid,
            value: JSON.parse(x.data, jsonReviver).name,
          })) as SelectData
        );
      }
    });
  }, []);

  useEffect(() => {
    if (currentOrganisationUuid) getOrganisationTags(currentOrganisationUuid);
  }, [currentOrganisationUuid, refreshIssuanceTransactions]);

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
    issuanceTransaction,
    actionsMenuOptionsList,
    showEditIssuanceTransactionPanel,
    showAddOrLinkIssuanceTransactionPanel,
    showActionsMenu,
    showEditColumnsPanel,
    handleCloseIssuanceTransactionPanel,
    setShowActionsMenu,
    setShowAddOrLinkIssuanceTransactionPanel,
    setShowEditColumnsPanel,
    exportTable,
    filterExportColumns,
    filterExportRows,
    defaultEditColumns: getEditColumns(),
    exportFileName: `${issuance?.uuid ? `${issuance.uuid}_` : ""}Inventory_Unit_Transactions_$`.replaceAll(" ", "_"),
    inventoryTransactionsEditColumnsLocalStorageItemName: `${user?.userUuid ? `${user?.userUuid}_` : ""}${LocalStorageConstants.PROJECT_INVENTORY_UNIT_TRANSACTIONS_PREFERENCES}`,
    refreshIssuanceTransactions,
    handleRefreshIssuancesAndIssuanceTransactions,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    hasData,
  };
};
